// =========================================================
// 🛠️ DEBUTS CROP — ADMIN DASHBOARD
// CYBERPUNK / NEON / TROPICAL 2026
// RESPONSIVE DESKTOP / TABLET / MOBILE
// =========================================================

import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import {
  LayoutDashboard,
  Package,
  Users,
  Store as StoreIcon,
  UserCheck,
  LogOut,
  RefreshCw,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  User,
  Menu,
  X,
  ChevronRight,
  Activity,
  Crown,
} from "lucide-react";

import ClientsManager from "../../components/dashboard/ClientsManager.js";
import ProductsManager from "../../components/dashboard/ProductsManager";
import StoreManager from "../../components/dashboard/StoreManager";
import WorkerManager from "../../components/dashboard/WorkerManager";
import Profile from "./Profile";

import SmokeEffect from "../../components/common/SmokeEffect.jsx";

import API from "../../services/api.js";

import bg from "../../assets/bg.png";
import logo from "../../assets/logo.png";

import "../../styles/dashboard.css";

// =========================================================
// CONFIG
// =========================================================

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://api.laesquinadelparques.com/api";

// =========================================================
// MENU
// =========================================================

const MENU_ITEMS = [
  {
    key: "products",
    label: "Productos",
    description: "Inventario y catálogo",
    icon: Package,
  },
  {
    key: "clients",
    label: "Clientes",
    description: "Usuarios registrados",
    icon: Users,
  },
  {
    key: "store",
    label: "Sedes",
    description: "Tiendas y ubicaciones",
    icon: StoreIcon,
  },
  {
    key: "worker",
    label: "Trabajadores",
    description: "Equipo interno",
    icon: UserCheck,
  },
];

// =========================================================
// ALERTS HOOK
// =========================================================

function useAlerts() {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!error && !success) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setError("");
      setSuccess("");
    }, 4500);

    return () => clearTimeout(timeout);
  }, [error, success]);

  return {
    error,
    setError,
    success,
    setSuccess,
  };
}

// =========================================================
// DASHBOARD
// =========================================================

export default function Dashboard({
  auth,
  onLogout,
}) {
  // =======================================================
  // USER
  // =======================================================

  const currentUser = auth?.user ?? {
    username: "Admin",
  };

  // =======================================================
  // GENERAL STATE
  // =======================================================

  const [loading, setLoading] = useState({
    fetch: false,
    add: false,
    update: false,
    delete: false,
  });

  const [tab, setTab] = useState("products");

  const [showProfile, setShowProfile] =
    useState(false);

  const [menuOpen, setMenuOpen] =
    useState(false);

  const [storeRefreshKey, setStoreRefreshKey] =
    useState(0);

  const [workerRefreshKey, setWorkerRefreshKey] =
    useState(0);

  const [products, setProducts] = useState([]);

  const {
    error,
    setError,
    success,
    setSuccess,
  } = useAlerts();

  const isLoading =
    Object.values(loading).some(Boolean);

  // =======================================================
  // CURRENT SECTION
  // =======================================================

  const currentSection =
    MENU_ITEMS.find(
      (item) => item.key === tab
    ) || MENU_ITEMS[0];

  const CurrentSectionIcon =
    currentSection.icon;

  // =======================================================
  // AUTH ERROR
  // =======================================================

  const handleAuthError = useCallback(() => {
    setError(
      "Sesión expirada. Inicia sesión nuevamente."
    );

    setTimeout(() => {
      onLogout();
    }, 1200);
  }, [onLogout, setError]);

  // =======================================================
  // API REQUEST
  // =======================================================

  const apiRequest = useCallback(
    async (
      method,
      endpoint,
      data = null,
      config = {}
    ) => {
      try {
        const response = await API({
          method,
          url: `${API_BASE_URL}${endpoint}`,
          data,
          ...config,
        });

        return response.data;
      } catch (err) {
        console.error(
          `❌ API ERROR (${endpoint}):`,
          err
        );

        if (err?.response?.status === 401) {
          handleAuthError();
          throw new Error("Unauthorized");
        }

        throw new Error(
          err?.response?.data?.message ||
            err?.response?.data?.detail ||
            err?.message ||
            "Error del servidor"
        );
      }
    },
    [handleAuthError]
  );

  // =======================================================
  // PRODUCTS — LOAD
  // =======================================================

  const fetchProducts = useCallback(
    async () => {
      setLoading((prev) => ({
        ...prev,
        fetch: true,
      }));

      setError("");

      try {
        const data = await apiRequest(
          "GET",
          "/products/"
        );

        const normalized =
          data?.data?.products ||
          data?.products ||
          data?.data ||
          [];

        setProducts(
          Array.isArray(normalized)
            ? normalized
            : []
        );
      } catch (err) {
        if (
          err.message !== "Unauthorized"
        ) {
          setError(
            err.message ||
              "No se pudieron cargar los productos"
          );
        }
      } finally {
        setLoading((prev) => ({
          ...prev,
          fetch: false,
        }));
      }
    },
    [apiRequest, setError]
  );

  // =======================================================
  // PRODUCTS — CREATE
  // =======================================================

  const addProduct = useCallback(
    async (product) => {
      setLoading((prev) => ({
        ...prev,
        add: true,
      }));

      setError("");
      setSuccess("");

      try {
        const formData = new FormData();

        Object.entries(product).forEach(
          ([key, value]) => {
            if (
              value !== undefined &&
              value !== null &&
              value !== ""
            ) {
              formData.append(key, value);
            }
          }
        );

        const data = await apiRequest(
          "POST",
          "/products/",
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

        const newProduct =
          data?.data ||
          data?.product ||
          data;

        setProducts((prev) => [
          newProduct,
          ...prev,
        ]);

        setSuccess(
          "Producto agregado correctamente"
        );
      } catch (err) {
        if (
          err.message !== "Unauthorized"
        ) {
          setError(
            err.message ||
              "No se pudo crear el producto"
          );
        }
      } finally {
        setLoading((prev) => ({
          ...prev,
          add: false,
        }));
      }
    },
    [apiRequest, setError, setSuccess]
  );

  // =======================================================
  // PRODUCTS — UPDATE
  // =======================================================

  const updateProduct = useCallback(
    async (id, updatedData) => {
      setLoading((prev) => ({
        ...prev,
        update: true,
      }));

      setError("");
      setSuccess("");

      try {
        const formData = new FormData();

        Object.entries(updatedData).forEach(
          ([key, value]) => {
            if (
              value !== undefined &&
              value !== null
            ) {
              formData.append(key, value);
            }
          }
        );

        const data = await apiRequest(
          "PUT",
          `/products/${id}/`,
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

        const updatedProduct =
          data?.data ||
          data?.product ||
          data;

        setProducts((prev) =>
          prev.map((product) =>
            product.id === id
              ? updatedProduct
              : product
          )
        );

        setSuccess(
          "Producto actualizado correctamente"
        );
      } catch (err) {
        if (
          err.message !== "Unauthorized"
        ) {
          setError(
            err.message ||
              "No se pudo actualizar el producto"
          );
        }
      } finally {
        setLoading((prev) => ({
          ...prev,
          update: false,
        }));
      }
    },
    [apiRequest, setError, setSuccess]
  );

  // =======================================================
  // PRODUCTS — DELETE
  // =======================================================

  const deleteProduct = useCallback(
    async (id) => {
      const confirmed = window.confirm(
        "¿Eliminar este producto?"
      );

      if (!confirmed) {
        return;
      }

      setLoading((prev) => ({
        ...prev,
        delete: true,
      }));

      setError("");
      setSuccess("");

      try {
        await apiRequest(
          "DELETE",
          `/products/${id}/`
        );

        setProducts((prev) =>
          prev.filter(
            (product) =>
              product.id !== id
          )
        );

        setSuccess(
          "Producto eliminado correctamente"
        );
      } catch (err) {
        if (
          err.message !== "Unauthorized"
        ) {
          setError(
            err.message ||
              "No se pudo eliminar el producto"
          );
        }
      } finally {
        setLoading((prev) => ({
          ...prev,
          delete: false,
        }));
      }
    },
    [apiRequest, setError, setSuccess]
  );

  // =======================================================
  // INITIAL PRODUCTS LOAD
  // =======================================================

  useEffect(() => {
    if (tab === "products") {
      fetchProducts();
    }
  }, [fetchProducts, tab]);

  // =======================================================
  // CLOSE MENU WITH ESC
  // =======================================================

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, []);

  // =======================================================
  // LOCK BODY WHEN MOBILE MENU OPEN
  // =======================================================

  useEffect(() => {
    const isMobile =
      window.innerWidth <= 800;

    if (menuOpen && isMobile) {
      document.body.classList.add(
        "dashboard-menu-open"
      );
    } else {
      document.body.classList.remove(
        "dashboard-menu-open"
      );
    }

    return () => {
      document.body.classList.remove(
        "dashboard-menu-open"
      );
    };
  }, [menuOpen]);

  // =======================================================
  // CHANGE SECTION
  // =======================================================

  const changeTab = (key) => {
    setTab(key);
    setShowProfile(false);
    setMenuOpen(false);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =======================================================
  // REFRESH CURRENT SECTION
  // =======================================================

  const refreshCurrentSection = () => {
    switch (tab) {
      case "products":
        fetchProducts();
        break;

      case "store":
        setStoreRefreshKey(
          (value) => value + 1
        );
        break;

      case "worker":
        setWorkerRefreshKey(
          (value) => value + 1
        );
        break;

      case "clients":
        break;

      default:
        break;
    }
  };

  // =======================================================
  // TOGGLE PROFILE
  // =======================================================

  const toggleProfile = () => {
    setShowProfile(
      (value) => !value
    );

    setMenuOpen(false);
  };

  // =======================================================
  // MENU
  // =======================================================

  const renderMenuButtons = () =>
    MENU_ITEMS.map(
      ({
        key,
        label,
        description,
        icon: Icon,
      }) => (
        <button
          key={key}
          type="button"
          className={
            tab === key
              ? "active"
              : ""
          }
          onClick={() =>
            changeTab(key)
          }
          aria-label={`Ir a ${label}`}
          aria-current={
            tab === key
              ? "page"
              : undefined
          }
        >
          <span className="admin__menuIcon">
            <Icon size={18} />
          </span>

          <span className="admin__menuText">
            <strong>
              {label}
            </strong>

            <small>
              {description}
            </small>
          </span>

          <ChevronRight
            size={15}
            className="admin__menuArrow"
          />
        </button>
      )
    );

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <div
      className="admin"
      style={{
        backgroundImage: `url(${bg})`,
      }}
    >
      {/* ===================================================
          VISUAL EFFECTS
          IMPORTANTE:
          ESTE WRAPPER NO DEBE OCUPAR ESPACIO
      =================================================== */}

      <div
        className="admin__effects"
        aria-hidden="true"
      >
        <SmokeEffect />
      </div>

      {/* ===================================================
          HEADER
      =================================================== */}

      <header className="admin__header">

        {/* BRAND */}

        <div className="admin__brand">

          <div className="admin__logoBox">
            <img
              src={logo}
              alt="Debuts Crop"
              className="admin__logo"
            />
          </div>

          <div className="admin__brandInfo">

            <h1>
              Debuts{" "}
              <span>CROP</span>
            </h1>

            <p>
              ADMIN CONTROL SYSTEM
            </p>

          </div>

        </div>

        {/* CURRENT SECTION */}

        <div className="admin__sectionInfo">

          <span className="admin__sectionIcon">
            <CurrentSectionIcon
              size={18}
            />
          </span>

          <div>
            <small>
              MÓDULO ACTIVO
            </small>

            <strong>
              {currentSection.label}
            </strong>
          </div>

        </div>

        {/* MOBILE MENU BUTTON */}

        <button
          type="button"
          className="mobileMenuBtn"
          onClick={() =>
            setMenuOpen(
              (value) => !value
            )
          }
          aria-label={
            menuOpen
              ? "Cerrar menú"
              : "Abrir menú"
          }
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <X size={22} />
          ) : (
            <Menu size={22} />
          )}
        </button>

        {/* DESKTOP ACTIONS */}

        <div className="admin__actions desktopOnly">

          <div
            className="admin__user"
            title={`Usuario: ${currentUser.username}`}
          >
            <span className="admin__userIcon">
              <ShieldCheck size={17} />
            </span>

            <div>
              <small>
                SESIÓN ACTIVA
              </small>

              <strong>
                {currentUser.username}
              </strong>
            </div>
          </div>

          <button
            type="button"
            className="admin__profileBtn"
            onClick={toggleProfile}
          >
            <User size={17} />
            Perfil
          </button>

          <button
            type="button"
            className="admin__refreshBtn"
            onClick={
              refreshCurrentSection
            }
            disabled={isLoading}
          >
            {loading.fetch ? (
              <Loader2
                size={17}
                className="spin"
              />
            ) : (
              <RefreshCw size={17} />
            )}

            Actualizar
          </button>

          <button
            type="button"
            className="logout-btn"
            onClick={onLogout}
          >
            <LogOut size={17} />
            Salir
          </button>

        </div>
      </header>

      {/* ===================================================
          MOBILE / DESKTOP MENU
      =================================================== */}

      <aside
        className={`admin__menu ${
          menuOpen
            ? "active"
            : ""
        }`}
        aria-hidden={
          window.innerWidth <= 800
            ? !menuOpen
            : false
        }
      >
        <div className="admin__menuTop">

          {/* MENU TITLE */}

          <div className="admin__menuTitle">

            <div className="admin__menuTitleIcon">
              <LayoutDashboard
                size={18}
              />
            </div>

            <div>
              <strong>
                Dashboard
              </strong>

              <small>
                Centro de control
              </small>
            </div>

          </div>

          {/* MENU ITEMS */}

          <div className="admin__menuItems">
            {renderMenuButtons()}
          </div>

        </div>

        {/* MOBILE ACTIONS */}

        <div className="mobileOnly admin__mobileActions">

          <div className="admin__mobileUser">

            <span>
              <ShieldCheck
                size={18}
              />
            </span>

            <div>
              <small>
                ADMINISTRADOR
              </small>

              <strong>
                {currentUser.username}
              </strong>
            </div>

          </div>

          <button
            type="button"
            className="admin__mobileBtn"
            onClick={toggleProfile}
          >
            <User size={18} />
            <span>
              Perfil
            </span>
          </button>

          <button
            type="button"
            className="admin__mobileBtn"
            onClick={() => {
              refreshCurrentSection();
              setMenuOpen(false);
            }}
            disabled={isLoading}
          >
            {loading.fetch ? (
              <Loader2
                size={18}
                className="spin"
              />
            ) : (
              <RefreshCw size={18} />
            )}

            <span>
              Actualizar
            </span>
          </button>

          <button
            type="button"
            className="admin__mobileBtn logout"
            onClick={() => {
              setMenuOpen(false);
              onLogout();
            }}
          >
            <LogOut size={18} />

            <span>
              Cerrar sesión
            </span>
          </button>

        </div>
      </aside>

      {/* ===================================================
          MAIN CONTENT
      =================================================== */}

      <main
        className="admin__content floatingPanel"
        aria-live="polite"
      >

        {/* TOP STATUS */}

        <div className="admin__contentTop">

          <div className="admin__breadcrumb">

            <span>
              DEBUTS CROP
            </span>

            <ChevronRight
              size={14}
            />

            <strong>
              {currentSection.label}
            </strong>

          </div>

          <div className="admin__systemStatus">

            <span className="admin__statusDot" />

            <Activity
              size={14}
            />

            SISTEMA ONLINE

          </div>

        </div>

        {/* LOADING */}

        {isLoading && (
          <div
            className="admin__loadingBar"
            role="status"
            aria-live="polite"
          >
            <Loader2
              size={17}
              className="spin"
            />

            <span>
              Procesando solicitud...
            </span>

            <span className="admin__loadingPulse" />
          </div>
        )}

        {/* ERROR */}

        {error && (
          <div
            className="admin-error"
            role="alert"
            aria-live="assertive"
          >
            <AlertTriangle
              size={18}
            />

            <span>
              {error}
            </span>
          </div>
        )}

        {/* SUCCESS */}

        {success && (
          <div
            className="admin-success"
            role="status"
            aria-live="polite"
          >
            <CheckCircle2
              size={18}
            />

            <span>
              {success}
            </span>
          </div>
        )}

        {/* MODULE HEADER */}

        {!showProfile && (
          <div className="admin__moduleHeader">

            <div>

              <div className="admin__moduleEyebrow">

                <Crown size={13} />

                DEBUTS CROP · ADMIN

              </div>

              <h2>
                {currentSection.label}
              </h2>

              <p>
                {currentSection.description}
              </p>

            </div>

            <div className="admin__moduleBadge">

              <span>
                2026
              </span>

              <small>
                CONTROL
              </small>

            </div>

          </div>
        )}

        {/* MODULE CONTENT */}

        <section className="admin__moduleContent">

          {showProfile ? (
            <Profile
              user={currentUser}
              onClose={() =>
                setShowProfile(false)
              }
            />
          ) : (
            <>
              {/* CLIENTS */}

              {tab === "clients" && (
                <ClientsManager />
              )}

              {/* PRODUCTS */}

              {tab === "products" && (
                <ProductsManager
                  products={products}
                  loading={isLoading}
                  addProduct={addProduct}
                  deleteProduct={
                    deleteProduct
                  }
                  updateProduct={
                    updateProduct
                  }
                  refreshProducts={
                    fetchProducts
                  }
                />
              )}

              {/* STORES */}

              {tab === "store" && (
                <StoreManager
                  refreshKey={
                    storeRefreshKey
                  }
                />
              )}

              {/* WORKERS */}

              {tab === "worker" && (
                <WorkerManager
                  refreshKey={
                    workerRefreshKey
                  }
                />
              )}
            </>
          )}

        </section>

      </main>
    </div>
  );
}